import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import {
  IGetEncryptionDetailsMutation,
  IMergePrepaidInput,
  IMergePrepaidPayload,
  useAddCreditAccountMutation,
  useGetEncryptionDetailsMutation,
} from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';

import { useAppStateRefresh } from '../app-state-refresh';
import { getValidPaymentMethodId } from '../hooks/getPaymentMethodsState/getValidPaymentMethodId';
import { getPreferredWalletOrder } from '../hooks/getPaymentMethodsState/utils';
import { usePayment } from '../hooks/use-payment';
import { IEncryptionResult } from '../integrations/orbital';
import {
  CardTypes,
  CurrentSelectedMethodType,
  IAddPaymentMethodOptions,
  IGetPaymentMethodsOptions,
  IPaymentDetails,
  IPaymentMethod,
  IPaymentPayload,
  IPaymentState,
  IReloadPrepaidCard,
} from '../types';
import { isCashApp } from '../utils';

import { initialPaymentState } from './initial-state';

export interface IPaymentContext {
  addPaymentMethod: (
    method: IPaymentPayload,
    options?: IAddPaymentMethodOptions,
    encryptionResult?: IEncryptionResult
  ) => Promise<string>;
  replacePaymentMethod: (
    accountIdentifier: string,
    method: IPaymentPayload,
    options?: IAddPaymentMethodOptions,
    encryptionResult?: IEncryptionResult
  ) => Promise<string>;
  checkoutPaymentMethod: IPaymentMethod | undefined;
  checkoutPaymentMethodId: string;

  // flags
  loading: boolean;
  savingPaymentMethods: boolean;
  canUseApplePay: boolean;
  canUseGooglePay: boolean;
  hasGetPaymentMethodsError: boolean;
  isFirstData: boolean;
  isOrbital: boolean;
  isQuickPayMethodSelected: boolean;

  recentlyAddedQuickPayMethodId: string | undefined;
  setRecentlyAddedQuickPayMethodId: Dispatch<SetStateAction<string | undefined>>;

  isCheckoutErrorsModalOpen: boolean;
  setIsCheckoutErrorsModalOpen: Dispatch<SetStateAction<boolean>>;

  isAddCreditCardModalVisible: boolean;
  setIsAddCreditCardModalVisible: (input: boolean) => void;

  isAddReloadPrepaidMethodModalVisible: boolean;
  setIsAddReloadPrepaidMethodModalVisible: (input: boolean) => void;

  isAddUpdateCardForReloadModalVisible: boolean;
  setIsAddUpdateCardForReloadModalVisible: (input: boolean) => void;

  setIsQuickPayMethodSelected: Dispatch<SetStateAction<boolean>>;

  defaultPaymentMethodId: string;
  defaultReloadPaymentMethodId: string;
  deletePaymentMethod: (accountId: string) => Promise<void>;
  encryptionResult: IEncryptionResult | undefined;

  // utils
  clearPaymentDetails(): void;
  getBalanceFromPaymentMethods: (prepaidPaymentMethod: IPaymentMethod) => number;
  getCounter?: () => Promise<void>;
  getEncryptionDetails: () => Promise<
    Pick<
      IGetEncryptionDetailsMutation['encryptionDetails'],
      'fdPublicKey' | 'fdApiKey' | 'fdAccessToken' | 'fdCustomerId' | 'algorithm'
    >
  >;
  getPaymentMethods: (options: IGetPaymentMethodsOptions) => Promise<void>;
  getPrepaidPaymentMethod: () => IPaymentMethod | null;
  getPrepaidCardNumber: () => string | null;

  mergePrepaidCardBalances: (input: IMergePrepaidInput) => Promise<IMergePrepaidPayload>;
  paymentMethods: IPaymentMethod[];
  paymentDetails: IPaymentDetails;
  paymentProcessor: string | null | undefined;
  prepaidReloadPaymentMethodId: string;
  reloadPaymentValues: IPaymentState;
  reloadPrepaidCard: (input: IReloadPrepaidCard) => Promise<number | undefined>;
  setCheckoutPaymentMethodId: (accountId: string) => void;
  setEncryptionResult: (encryptionResult: IEncryptionResult | undefined) => void;
  setDefaultPaymentMethodId: (accountId: string) => void;
  setDefaultReloadPaymentMethodId: (accountId: string) => void;
  setPrepaidReloadPaymentMethodId: (accountId: string) => void;
  setLastCommitOrderErrorDate: Dispatch<SetStateAction<number | null>>;
  lastCommitOrderErrorDate: number | null;
  setSelected?: (fdAccountId: string) => void;
  setReloadPaymentValues: Dispatch<SetStateAction<IPaymentState>>;
  storePaymentDetails: (data: IPaymentDetails) => void;
  transformPaymentValues: ({
    paymentValues,
    paymentFieldVariations,
  }: {
    paymentValues: IPaymentState;
    paymentFieldVariations: PaymentFieldVariations;
  }) => IPaymentPayload;
  setLoading: (input: boolean) => void;

  orderGrandTotal: number;
  setOrderGrandTotal: (input: number) => void;
  setPaymentMethodByNativePriority(): void;
  noPaymentMethodsAvailable: boolean;

  processingPayment: boolean;
  showPaymentFooter: boolean;

  setProcessingPayment: Dispatch<SetStateAction<boolean>>;
  setShowPaymentFooter: Dispatch<SetStateAction<boolean>>;

  accountToDelete: string;
  /** actually means "account to edit" */
  setAccountToDelete: (param: string) => void;
}

export const PaymentContext = createContext<IPaymentContext>({} as IPaymentContext);
export const usePaymentContext = (): IPaymentContext => useContext(PaymentContext);

export function PaymentProvider(props: IBaseProps) {
  const { feCountryCode } = useLocale();
  const { updateUserInfo, user, isReAuthenticating } = useAuthContext();
  const [getEncryptionDetailsMutation] = useGetEncryptionDetailsMutation();
  const [addCreditAccountMutation] = useAddCreditAccountMutation();

  const [paymentDetails, setPaymentDetails] = useState<IPaymentDetails>({});
  const [isQuickPayMethodSelected, setIsQuickPayMethodSelected] = useState(false);
  const [recentlyAddedQuickPayMethodId, setRecentlyAddedQuickPayMethodId] = useState<string>();
  const [orderGrandTotal, setOrderGrandTotal] = useState<number | null>(null);
  const [lastCommitOrderErrorDate, setLastCommitOrderErrorDate] = useState<number | null>(null);

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [showPaymentFooter, setShowPaymentFooter] = useState<boolean>(false);
  const [isAddCreditCardModalVisible, setIsAddCreditCardModalVisible] = useState(false);
  const [isAddUpdateCardForReloadModalVisible, setIsAddUpdateCardForReloadModalVisible] = useState(
    false
  );
  const [isAddReloadPrepaidMethodModalVisible, setIsAddReloadPrepaidMethodModalVisible] = useState(
    false
  );
  const [accountToDelete, setAccountToDelete] = useState('');
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Payment Error',
  });

  const [reloadPaymentValues, setReloadPaymentValues] = useState(
    initialPaymentState({ billingCountry: feCountryCode, userDetailsName: user?.details?.name })
  );

  const storePaymentDetails = useCallback((data: IPaymentDetails): void => {
    setPaymentDetails(data);
  }, []);

  const clearPaymentDetails = useCallback(() => {
    setPaymentDetails({});
  }, []);

  const {
    addPaymentMethod,
    replacePaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    deletePaymentMethod,
    encryptionResult,
    setEncryptionResult,
    getEncryptionDetails,
    getBalanceFromPaymentMethods,
    getPaymentMethods,
    getPrepaidCardNumber,
    getPrepaidPaymentMethod,
    hasGetPaymentMethodsError,
    isFirstData,
    isOrbital,
    loading,
    savingPaymentMethods,
    mergePrepaidCardBalances,
    paymentMethods,
    paymentProcessor,
    prepaidReloadPaymentMethodId,
    reloadPrepaidCard,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    transformPaymentValues,
    setLoading,
    noPaymentMethodsAvailable,
  } = usePayment({
    getEncryptionDetailsMutation,
    openErrorDialog,
    user,
    isReAuthenticating,
    updateUserInfo,
    addCreditAccountMutation,
    paymentDetails,
    isQuickPayMethodSelected,
  });

  useAppStateRefresh(getPaymentMethods, user?.cognitoId);

  const setPaymentMethodByNativePriority = useCallback(() => {
    const { first, second } = getPreferredWalletOrder();

    const walletCanUse: Record<string, boolean> = {
      [CardTypes.APPLE_PAY]: canUseApplePay,
      [CardTypes.GOOGLE_PAY]: canUseGooglePay,
    };

    // Try the platform-preferred wallet first, then the other
    for (const wallet of [first, second]) {
      if (walletCanUse[wallet]) {
        setCheckoutPaymentMethodId(wallet);
        setDefaultPaymentMethodId(wallet);
        return;
      }
    }

    // Remove CashApp from the list
    const paymentMethodsWithoutCashApp = paymentMethods.filter(
      method => !isCashApp(method.accountIdentifier || method.fdAccountId)
    );
    const validPaymentMethodId = getValidPaymentMethodId({
      canUseApplePay,
      canUseGooglePay,
      // If gift card payment is available, it will return this method;
      // otherwise, it will select the first available one from the list.
      paymentMethodId: CardTypes.GIFT_CARD,
      paymentMethods: paymentMethodsWithoutCashApp,
      returnFirstValid: true,
    });

    if (validPaymentMethodId) {
      setCheckoutPaymentMethodId(validPaymentMethodId);
      setDefaultPaymentMethodId(validPaymentMethodId);
    }
  }, [
    canUseApplePay,
    canUseGooglePay,
    paymentMethods,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
  ]);

  // when clearing the paymentDetails, and the One Time Payment Method was selected, we need to update
  // the checkoutPaymentMethodId with the user's default payment method
  useEffect(() => {
    if (
      checkoutPaymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD &&
      !paymentDetails.state
    ) {
      setPaymentMethodByNativePriority();
    }
  }, [
    checkoutPaymentMethodId,
    defaultPaymentMethodId,
    paymentDetails.state,
    setCheckoutPaymentMethodId,
    setPaymentMethodByNativePriority,
  ]);

  const value = {
    addPaymentMethod,
    replacePaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    clearPaymentDetails,
    encryptionResult,
    setEncryptionResult,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    deletePaymentMethod,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    hasGetPaymentMethodsError,
    isFirstData,
    isOrbital,
    loading,
    savingPaymentMethods,
    lastCommitOrderErrorDate,
    mergePrepaidCardBalances,
    paymentMethods,
    paymentProcessor,
    paymentDetails,
    prepaidReloadPaymentMethodId,
    reloadPaymentValues,
    reloadPrepaidCard,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setLastCommitOrderErrorDate,
    setPrepaidReloadPaymentMethodId,
    storePaymentDetails,
    setReloadPaymentValues,
    transformPaymentValues,
    setLoading,
    isQuickPayMethodSelected,
    setIsQuickPayMethodSelected,
    recentlyAddedQuickPayMethodId,
    setRecentlyAddedQuickPayMethodId,
    orderGrandTotal: orderGrandTotal ?? 0,
    setOrderGrandTotal,
    setPaymentMethodByNativePriority,
    noPaymentMethodsAvailable,

    isAddUpdateCardForReloadModalVisible,
    setIsAddUpdateCardForReloadModalVisible,

    isCheckoutErrorsModalOpen,
    processingPayment,
    setIsCheckoutErrorsModalOpen,
    setProcessingPayment,
    setShowPaymentFooter,
    isAddCreditCardModalVisible,
    setIsAddCreditCardModalVisible,
    isAddReloadPrepaidMethodModalVisible,
    setIsAddReloadPrepaidMethodModalVisible,
    accountToDelete,
    setAccountToDelete,
    showPaymentFooter,
  };

  return (
    <PaymentContext.Provider value={value}>
      {props.children}
      <ErrorDialog />
    </PaymentContext.Provider>
  );
}

export default PaymentContext.Consumer;
